// ContextMapCandidatesAdminPage.js — Phase PEEK.1.D1
//
// Admin-only review queue at /admin/context-map-candidates. Drives the
// PEEK distillation pipeline:
//
//   1. Per-recipe roll-up — for each recipe with >=1 trajectory uploaded,
//      show count + last-distilled date + current map size + [Distill] button.
//   2. Pending candidates — newly-distilled maps awaiting admin review.
//      Per-candidate: [Run Discriminator] | [Promote] | [Reject].
//   3. History — recently promoted/rejected candidates with commit link
//      or rejection reason.
//
// Cloned from TemplateCandidatesAdminPage.js. Scoped down — no SUGGESTED_GAPS
// panel (PEEK has no "seed" flow; trajectories come from real user sessions).
//
// Auth: same UID allowlist + custom-claim pattern as TemplateCandidatesAdminPage.

import React, { useState, useEffect, useCallback } from 'react';
import { getAuth } from 'firebase/auth';
import { getFirestore, collection, query, orderBy, limit, getDocs } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import './App.css';

const ADMIN_UIDS = ['GI1GHQ7wpLQKjJyf9QRQATyw5e33', 'vGB6BL3iJfh32aWZ0HA93nUV2013'];

// Hard cap on candidates loaded into the page (pending + history combined).
// PEEK.1 expects single-digit candidates/week — 200 is generous and matches
// the template-candidates page's cap.
const CANDIDATE_LIMIT = 200;

// Recent-trajectories sample size for the per-recipe roll-up. The query is
// `orderBy uploadedAt desc limit N`; we dedupe by recipeID client-side. As
// long as `recipeID` count <= N, the roll-up captures every active recipe.
const TRAJECTORY_ROLLUP_SAMPLE = 500;

const STATUS_FILTERS = [
  { id: 'pending',  label: 'Pending',  predicate: (c) => !c.adminDecision },
  { id: 'promoted', label: 'Promoted', predicate: (c) => c.adminDecision?.kind === 'promoted' },
  { id: 'rejected', label: 'Rejected', predicate: (c) => c.adminDecision?.kind === 'rejected' },
  { id: 'all',      label: 'All',      predicate: () => true },
];

export default function ContextMapCandidatesAdminPage() {
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  const [candidates, setCandidates] = useState([]);
  const [recipeRollup, setRecipeRollup] = useState([]);  // [{recipeID, trajectoryCount, lastUploadedAt, currentMap}]
  const [statusFilter, setStatusFilter] = useState('pending');
  const [busyAction, setBusyAction] = useState(null);     // {kind, candidateID|recipeID}
  const [actionLog, setActionLog] = useState([]);
  const [rejectReason, setRejectReason] = useState({});   // candidateID -> string

  // ── Auth ────────────────────────────────────────────────────────────────────

  useEffect(() => {
    const auth = getAuth();
    const unsub = auth.onAuthStateChanged(async (u) => {
      setUser(u);
      if (!u) { setIsAdmin(false); setLoading(false); return; }
      const claims = (await u.getIdTokenResult()).claims || {};
      setIsAdmin(ADMIN_UIDS.includes(u.uid) || claims.role === 'admin');
      if (!ADMIN_UIDS.includes(u.uid) && claims.role !== 'admin') setLoading(false);
    });
    return () => unsub();
  }, []);

  // ── Data refresh ────────────────────────────────────────────────────────────

  const refresh = useCallback(async () => {
    if (!isAdmin) return;
    setLoading(true);
    try {
      const db = getFirestore();

      // Load candidates (full list, filtered client-side by status pill).
      const candQ = query(
        collection(db, 'context_map_candidates'),
        orderBy('createdAt', 'desc'),
        limit(CANDIDATE_LIMIT)
      );
      const candSnap = await getDocs(candQ);
      const cands = candSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      setCandidates(cands);

      // Load recent trajectories to compute per-recipe rollup.
      const trajQ = query(
        collection(db, 'context_map_trajectories'),
        orderBy('uploadedAt', 'desc'),
        limit(TRAJECTORY_ROLLUP_SAMPLE)
      );
      const trajSnap = await getDocs(trajQ);
      const perRecipe = new Map(); // recipeID -> {count, lastUploadedAt}
      trajSnap.docs.forEach(d => {
        const data = d.data();
        const rid = data.recipeID;
        if (typeof rid !== 'string' || !rid) return;
        const entry = perRecipe.get(rid) || { count: 0, lastUploadedAt: null };
        entry.count += 1;
        const ts = data.uploadedAt?.toMillis?.() ?? null;
        if (ts && (entry.lastUploadedAt === null || ts > entry.lastUploadedAt)) {
          entry.lastUploadedAt = ts;
        }
        perRecipe.set(rid, entry);
      });

      // For each recipe, fetch the currently-promoted map (raw.githubusercontent)
      // so we can show its byte size + last-promoted timestamp. Fire-and-forget;
      // any 404 means "no map promoted yet".
      const rollup = await Promise.all(
        [...perRecipe.entries()].map(async ([recipeID, info]) => {
          let currentMap = null;
          try {
            const r = await fetch(
              `https://raw.githubusercontent.com/LawrenceNorman/mchatai-source/main/harness/context-maps/${recipeID}.json`
            );
            if (r.ok) {
              const text = await r.text();
              const parsed = JSON.parse(text);
              currentMap = {
                bytes: text.length,
                version: parsed.version,
                promotedAt: parsed._meta?.promotedAt || null,
                candidateID: parsed._meta?.candidateID || null
              };
            }
          } catch { /* no map yet */ }
          return {
            recipeID,
            trajectoryCount: info.count,
            lastUploadedAt: info.lastUploadedAt,
            currentMap
          };
        })
      );
      // Sort: highest trajectoryCount first (most distillation-ready).
      rollup.sort((a, b) => b.trajectoryCount - a.trajectoryCount);
      setRecipeRollup(rollup);
    } catch (err) {
      pushLog('error', `Refresh failed: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => { if (isAdmin) refresh(); }, [isAdmin, refresh]);

  function pushLog(kind, message) {
    setActionLog(prev => [{ ts: new Date().toLocaleTimeString(), kind, message }, ...prev].slice(0, 30));
  }

  async function callFn(name, payload) {
    const fn = httpsCallable(getFunctions(), name);
    const result = await fn(payload);
    return result.data;
  }

  // ── Actions ─────────────────────────────────────────────────────────────────

  async function distill(recipeID) {
    setBusyAction({ kind: 'distill', target: recipeID });
    try {
      const data = await callFn('distillContextMap', { recipeID });
      pushLog('ok', `Distilled new candidate ${data.candidateID.slice(0, 8)} for ${recipeID} (${data.mapBytes}B, $${data.distillerCost?.toFixed(4)}, ${data.provider}/${data.model})`);
      await refresh();
    } catch (err) {
      pushLog('error', `distillContextMap(${recipeID}) failed: ${err.message}`);
    } finally {
      setBusyAction(null);
    }
  }

  async function runDiscriminator(c) {
    setBusyAction({ kind: 'discriminator', target: c.candidateID });
    try {
      const data = await callFn('runContextMapDiscriminator', { candidateID: c.candidateID });
      pushLog('ok', `Discriminator: ${data.decision} @ ${(data.confidence * 100).toFixed(0)}% — ${(data.reasoning || '').slice(0, 80)}`);
      await refresh();
    } catch (err) {
      pushLog('error', `runContextMapDiscriminator failed: ${err.message}`);
    } finally {
      setBusyAction(null);
    }
  }

  async function promote(c) {
    if (!window.confirm(`Promote candidate ${c.candidateID.slice(0, 8)} for ${c.recipeID}? This commits to mchatai-source/harness/context-maps/${c.recipeID}.json and overwrites any existing map.`)) return;
    setBusyAction({ kind: 'promote', target: c.candidateID });
    try {
      const data = await callFn('promoteContextMapCandidate', { candidateID: c.candidateID });
      pushLog('ok', `Promoted → ${data.commitURL}`);
      await refresh();
    } catch (err) {
      pushLog('error', `promote failed: ${err.message}`);
    } finally {
      setBusyAction(null);
    }
  }

  async function reject(c) {
    const reason = rejectReason[c.candidateID]?.trim() || '';
    if (!reason) {
      alert('Add a rejection reason before clicking Reject.');
      return;
    }
    setBusyAction({ kind: 'reject', target: c.candidateID });
    try {
      const data = await callFn('rejectContextMapCandidate', { candidateID: c.candidateID, reason });
      pushLog('ok', `Rejected: ${data.reason}`);
      await refresh();
    } catch (err) {
      pushLog('error', `reject failed: ${err.message}`);
    } finally {
      setBusyAction(null);
    }
  }

  // ── Render ──────────────────────────────────────────────────────────────────

  if (!user) {
    return (
      <div style={pageStyle}>
        <h1>Context Map Candidates</h1>
        <p>Sign in with an admin account to view this page.</p>
      </div>
    );
  }
  if (!isAdmin) {
    return (
      <div style={pageStyle}>
        <h1>Context Map Candidates</h1>
        <p>Your account ({user.email || user.uid}) does not have admin access. Contact a platform admin to be added to the allowlist.</p>
      </div>
    );
  }

  const filtered = candidates.filter(STATUS_FILTERS.find(f => f.id === statusFilter)?.predicate || (() => true));

  return (
    <div style={pageStyle}>
      <h1 style={{ marginBottom: 4 }}>Context Map Candidates (PEEK.1)</h1>
      <p style={{ color: '#777', marginTop: 0 }}>
        Distill accumulated trajectories per recipe → review the candidate map → promote into mchatai-source or reject.{' '}
        Spec: <code>docs/PHASE_PEEK_CONTEXT_MAP_SPEC.md</code>.
      </p>

      <div style={{ margin: '16px 0' }}>
        <button onClick={refresh} disabled={loading}>{loading ? 'Refreshing…' : 'Refresh'}</button>
      </div>

      {/* Section 1: per-recipe roll-up */}
      <section style={sectionStyle}>
        <h2>Per-recipe trajectory roll-up</h2>
        {recipeRollup.length === 0 ? (
          <p style={{ color: '#888' }}>No trajectories uploaded yet. Once opted-in users complete sessions, recipes will appear here.</p>
        ) : (
          <table style={tableStyle}>
            <thead>
              <tr>
                <th>recipeID</th>
                <th style={{ textAlign: 'right' }}>trajectories</th>
                <th>last upload</th>
                <th>current map</th>
                <th>action</th>
              </tr>
            </thead>
            <tbody>
              {recipeRollup.map(r => (
                <tr key={r.recipeID}>
                  <td><code>{r.recipeID}</code></td>
                  <td style={{ textAlign: 'right' }}>{r.trajectoryCount}</td>
                  <td>{r.lastUploadedAt ? new Date(r.lastUploadedAt).toLocaleString() : '—'}</td>
                  <td>
                    {r.currentMap
                      ? <>v{r.currentMap.version}, {r.currentMap.bytes}B<br /><span style={{ fontSize: 11, color: '#888' }}>{r.currentMap.promotedAt ? new Date(r.currentMap.promotedAt).toLocaleDateString() : ''}</span></>
                      : <span style={{ color: '#888' }}>none</span>
                    }
                  </td>
                  <td>
                    <button
                      onClick={() => distill(r.recipeID)}
                      disabled={busyAction?.target === r.recipeID}
                      style={{
                        background: '#1565c0',
                        color: '#fff',
                        border: 'none',
                        padding: '8px 18px',
                        borderRadius: 4,
                        fontWeight: 600,
                        cursor: busyAction?.target === r.recipeID ? 'wait' : 'pointer',
                        opacity: busyAction?.target === r.recipeID ? 0.6 : 1,
                        boxShadow: '0 1px 2px rgba(0,0,0,0.15)'
                      }}
                    >
                      {busyAction?.kind === 'distill' && busyAction?.target === r.recipeID ? 'Distilling…' : 'Distill →'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/* Section 2 + 3: candidates queue */}
      <section style={sectionStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
          <h2 style={{ margin: 0 }}>Candidates</h2>
          {STATUS_FILTERS.map(f => (
            <button
              key={f.id}
              onClick={() => setStatusFilter(f.id)}
              style={{
                background: statusFilter === f.id ? '#333' : '#eee',
                color: statusFilter === f.id ? '#fff' : '#333',
                border: 'none',
                padding: '4px 10px',
                borderRadius: 4,
                cursor: 'pointer',
                fontSize: 13
              }}
            >{f.label} ({candidates.filter(f.predicate).length})</button>
          ))}
        </div>

        {filtered.length === 0 ? (
          <p style={{ color: '#888' }}>No candidates match the current filter.</p>
        ) : (
          <div>
            {filtered.map(c => (
              <article key={c.candidateID} style={candidateStyle}>
                <header style={candidateHeaderStyle}>
                  <div>
                    <code style={{ fontSize: 12 }}>{c.candidateID?.slice(0, 8)}</code>{' · '}
                    <strong>{c.recipeID}</strong>{' · '}
                    {c.trajectoriesAnalyzed} trajectories{' · '}
                    {c.mapBytes}B{' · '}
                    ${c.distillerCost?.toFixed(4)}{' '}
                    <span style={{ color: '#888', fontSize: 12 }}>
                      ({c.distillerProvider}/{c.distillerModel})
                    </span>
                  </div>
                  <div style={{ fontSize: 12, color: '#888' }}>
                    {c.generatedAt ? new Date(c.generatedAt).toLocaleString() : ''}
                  </div>
                </header>

                {/* Map preview */}
                <details style={{ marginTop: 8 }}>
                  <summary style={{ cursor: 'pointer', fontSize: 13 }}>Map preview</summary>
                  <pre style={preStyle}>{JSON.stringify(c.map, null, 2)}</pre>
                </details>

                {/* Discriminator scores */}
                {c.discriminator && (
                  <div style={discBlockStyle}>
                    <div>
                      <strong>Discriminator: {c.discriminator.decision}</strong>{' '}
                      @ {(c.discriminator.confidence * 100).toFixed(0)}%{' '}
                      <span style={{ color: '#888', fontSize: 12 }}>
                        ({c.discriminator.provider}/{c.discriminator.model})
                      </span>
                    </div>
                    <div style={{ fontSize: 12, marginTop: 4 }}>{c.discriminator.reasoning}</div>
                    <div style={{ fontSize: 11, marginTop: 4, color: '#666' }}>
                      orientation={c.discriminator.subScores?.orientationQuality?.toFixed(2)}{' · '}
                      transferability={c.discriminator.subScores?.transferability?.toFixed(2)}{' · '}
                      redundancy={c.discriminator.subScores?.redundancy?.toFixed(2)}{' · '}
                      budget={c.discriminator.subScores?.budgetCompliance?.toFixed(2)}
                    </div>
                  </div>
                )}

                {/* Admin decision */}
                {c.adminDecision?.kind === 'promoted' && (
                  <div style={{ ...decisionStyle, background: '#e8f5e8' }}>
                    Promoted by <code>{c.adminDecision.promotedByAdminUid?.slice(0, 8)}</code>{' '}
                    on {new Date(c.adminDecision.promotedAt).toLocaleString()}.{' '}
                    <a href={c.adminDecision.commitURL} target="_blank" rel="noreferrer">commit</a>{' · '}
                    <code style={{ fontSize: 11 }}>{c.adminDecision.filePath}</code>
                  </div>
                )}
                {c.adminDecision?.kind === 'rejected' && (
                  <div style={{ ...decisionStyle, background: '#fbe9e7' }}>
                    Rejected by <code>{c.adminDecision.rejectedByAdminUid?.slice(0, 8)}</code>{' '}
                    on {new Date(c.adminDecision.rejectedAt).toLocaleString()}.{' '}
                    Reason: {c.adminDecision.reason || '(none)'}
                  </div>
                )}

                {/* Pending: action buttons */}
                {!c.adminDecision && (
                  <div style={actionRowStyle}>
                    <button
                      onClick={() => runDiscriminator(c)}
                      disabled={busyAction?.target === c.candidateID}
                      style={{
                        background: '#5e35b1',
                        color: '#fff',
                        border: 'none',
                        padding: '8px 14px',
                        borderRadius: 4,
                        fontWeight: 600,
                        cursor: busyAction?.target === c.candidateID ? 'wait' : 'pointer',
                        opacity: busyAction?.target === c.candidateID ? 0.6 : 1
                      }}
                    >
                      {busyAction?.kind === 'discriminator' && busyAction?.target === c.candidateID ? 'Running…' : (c.discriminator ? 'Re-run Discriminator' : 'Run Discriminator')}
                    </button>
                    <button
                      onClick={() => promote(c)}
                      disabled={busyAction?.target === c.candidateID}
                      style={{ background: '#2e7d32', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: 4 }}
                    >
                      {busyAction?.kind === 'promote' && busyAction?.target === c.candidateID ? 'Promoting…' : 'Promote'}
                    </button>
                    <input
                      type="text"
                      value={rejectReason[c.candidateID] || ''}
                      onChange={(e) => setRejectReason(prev => ({ ...prev, [c.candidateID]: e.target.value }))}
                      placeholder="rejection reason"
                      style={{ flex: 1, padding: 4, fontSize: 13 }}
                    />
                    <button
                      onClick={() => reject(c)}
                      disabled={busyAction?.target === c.candidateID}
                      style={{ background: '#c62828', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: 4 }}
                    >
                      {busyAction?.kind === 'reject' && busyAction?.target === c.candidateID ? 'Rejecting…' : 'Reject'}
                    </button>
                  </div>
                )}
              </article>
            ))}
          </div>
        )}
      </section>

      {/* Action log */}
      <section style={sectionStyle}>
        <h2>Recent actions</h2>
        {actionLog.length === 0 ? (
          <p style={{ color: '#888' }}>(empty)</p>
        ) : (
          <ul style={{ fontFamily: 'monospace', fontSize: 12, listStyle: 'none', padding: 0 }}>
            {actionLog.map((entry, i) => (
              <li key={i} style={{ color: entry.kind === 'error' ? '#c62828' : '#2e7d32' }}>
                [{entry.ts}] {entry.message}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

// ── Styles (inline to keep external CSS noise low) ──────────────────────────

const pageStyle = { padding: 24, maxWidth: 1200, margin: '0 auto', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' };
const sectionStyle = { marginBottom: 32, padding: 16, background: '#fafafa', borderRadius: 8 };
const tableStyle = { width: '100%', borderCollapse: 'collapse' };
const candidateStyle = { marginBottom: 16, padding: 12, background: '#fff', border: '1px solid #ddd', borderRadius: 6 };
const candidateHeaderStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 };
const preStyle = { background: '#f5f5f5', padding: 8, fontSize: 11, overflowX: 'auto', borderRadius: 4, maxHeight: 400 };
const discBlockStyle = { marginTop: 8, padding: 8, background: '#fff8e1', borderRadius: 4 };
const decisionStyle = { marginTop: 8, padding: 8, borderRadius: 4, fontSize: 13 };
const actionRowStyle = { marginTop: 8, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' };
